import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { CONFIG } from './_barrel.js';


        // Fleet map for the Tracking tab: every live tracker's latest position
        // as a labelled, status-coloured pin, plus the selected tracker's route
        // (retrace) with start/end markers and an optional replay "ghost".
        //
        // Street tiles come through our own cache proxy (/api/v1/aea/tiles) so a
        // tracked person's whereabouts are never requested from a third-party
        // tile server by the analyst's browser; satellite imagery is optional.
        const STATUS_COLOR = {
            live: '#6ecb90', stale: '#e6b23a', quiet: '#7fa7c9', dark: '#ec7175', never: '#8a8a92', lapsed: '#55555c',
        };
        const FENCE_COLOR = '#c9a96e';
        const ROUTE_COLOR = '#66c2ff';

        function escapeHtml(s) {
            return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
                '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
            }[c]));
        }

        // Route segmentation. Consecutive fixes are joined only when the phone
        // was plausibly reporting continuously: no silence longer than GAP_S
        // and no implied speed above MAX_SPEED (a "teleport" from a bad fix or
        // a long gap). Fixes coarser than MAX_ACC metres are kept off the line.
        const GAP_S = 15 * 60, MAX_SPEED = 70 /* m/s ≈ 250 km/h */, MAX_ACC = 500;
        function haversineM(a, b) {
            const R = 6371000, r = Math.PI / 180;
            const dp = (b.lat - a.lat) * r, dl = (b.lon - a.lon) * r;
            const x = Math.sin(dp / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dl / 2) ** 2;
            return 2 * R * Math.asin(Math.sqrt(x));
        }
        function splitRoute(points) {
            const segments = [], gaps = [], coarse = [];
            let cur = [];
            for (const p of points) {
                if (p.accuracy_m != null && p.accuracy_m > MAX_ACC) { coarse.push(p); continue; }
                if (cur.length) {
                    const prev = cur[cur.length - 1];
                    const dt = (new Date(p.recorded_at) - new Date(prev.recorded_at)) / 1000;
                    const d = haversineM(prev, p);
                    if (dt > GAP_S || (dt > 0 && d / dt > MAX_SPEED) || (dt <= 0 && d > 200)) {
                        gaps.push([prev, p]); segments.push(cur); cur = [];
                    }
                }
                cur.push(p);
            }
            if (cur.length) segments.push(cur);
            return { segments, gaps, coarse };
        }
        function fmtGap(a, b) {
            const s = Math.max(0, (new Date(b.recorded_at) - new Date(a.recorded_at)) / 1000);
            if (s < 60) return `${Math.round(s)} s`;
            if (s < 3600) return `${Math.round(s / 60)} min`;
            if (s < 86400) return `${(s / 3600).toFixed(1)} h`;
            return `${(s / 86400).toFixed(1)} days`;
        }

        function pinIcon(color, label, selected) {
            const size = selected ? 18 : 14;
            return L.divIcon({
                className: 'veil-pin', iconSize: [22, 22], iconAnchor: [11, 11],
                html: `<div style="position:relative;width:22px;height:22px;display:flex;align-items:center;justify-content:center">`
                    + `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:3px solid #fff;`
                    + `box-shadow:0 0 0 2px rgba(0,0,0,.6),0 0 10px ${color}"></div>`
                    + (label ? `<div style="position:absolute;left:24px;top:1px;white-space:nowrap;font:600 11px/1.2 -apple-system,`
                        + `BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#fff;background:rgba(0,0,0,.7);padding:2px 6px;`
                        + `border-radius:4px;${selected ? 'outline:1px solid ' + color + ';' : ''}">${escapeHtml(label)}</div>` : '')
                    + `</div>`,
            });
        }

        function TrackingMap({ devices, selectedId, route, replayIndex, onSelect, fitAllKey, fitRouteKey, height = 480,
                               fences = [], onMapClick = null, placing = false, focus = null }) {
            const ref = useRef(null);
            const mapRef = useRef(null);
            const layersRef = useRef({});
            const didFitRef = useRef(false);
            const onSelectRef = useRef(onSelect);
            onSelectRef.current = onSelect;
            const onMapClickRef = useRef(onMapClick);
            onMapClickRef.current = onMapClick;

            // Create the map once.
            useEffect(() => {
                if (!ref.current || mapRef.current) return undefined;
                const map = L.map(ref.current, {
                    preferCanvas: true, zoomControl: true, attributionControl: true, worldCopyJump: true,
                });
                const streets = L.tileLayer(`${CONFIG.apiBase}/api/v1/aea/tiles/{z}/{x}/{y}.png`, {
                    maxZoom: 19, attribution: '&copy; OpenStreetMap contributors', crossOrigin: true,
                });
                const sat = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                    maxZoom: 19, attribution: 'Imagery &copy; Esri',
                });
                const labels = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
                    maxZoom: 19, attribution: 'Labels &copy; Esri',
                });
                streets.addTo(map);
                L.control.layers(
                    { 'Streets': streets, 'Satellite': sat, 'Satellite + labels': L.layerGroup([sat, labels]) },
                    {}, { collapsed: true, position: 'topright' }).addTo(map);
                map.setView([20, 0], 2);
                // Pins cluster when many phones sit on top of each other (a
                // team at one venue); a cluster expands on click.
                const pins = (L.markerClusterGroup
                    ? L.markerClusterGroup({ maxClusterRadius: 40, showCoverageOnHover: false, spiderfyOnMaxZoom: true, disableClusteringAtZoom: 17 })
                    : L.layerGroup()).addTo(map);
                layersRef.current = {
                    fences: L.layerGroup().addTo(map),
                    pins,
                    route: L.layerGroup().addTo(map),
                    ghost: L.layerGroup().addTo(map),
                };
                map.on('click', e => { if (onMapClickRef.current) onMapClickRef.current({ lat: e.latlng.lat, lon: e.latlng.lng }); });
                mapRef.current = map;
                const onResize = () => map.invalidateSize();
                window.addEventListener('resize', onResize);
                setTimeout(onResize, 0);
                return () => {
                    window.removeEventListener('resize', onResize);
                    map.remove(); mapRef.current = null; didFitRef.current = false;
                };
            }, []);

            // Geofences (zones): dashed gold circles with the name on hover.
            useEffect(() => {
                const layers = layersRef.current;
                if (!layers.fences) return;
                layers.fences.clearLayers();
                (fences || []).forEach(f => {
                    L.circle([f.lat, f.lon], {
                        radius: f.radius_m, color: FENCE_COLOR, weight: 2, dashArray: '6 6',
                        fillColor: FENCE_COLOR, fillOpacity: f.active === false ? 0.02 : 0.08,
                    }).bindTooltip(`${f.name} · ${Math.round(f.radius_m)} m · alert on ${f.alert_on}${f.device_id ? '' : ' · all trackers'}`)
                      .addTo(layers.fences);
                });
            }, [fences]);
            // Jump to a point (a stop the analyst clicked).
            useEffect(() => {
                const map = mapRef.current;
                if (!map || !focus) return;
                map.setView([focus.lat, focus.lon], Math.max(map.getZoom(), 16));
            }, [focus]);
            // Crosshair cursor while placing a zone.
            useEffect(() => {
                const map = mapRef.current;
                if (!map) return;
                map.getContainer().style.cursor = placing ? 'crosshair' : '';
            }, [placing]);

            // Device pins.
            useEffect(() => {
                const map = mapRef.current, layers = layersRef.current;
                if (!map || !layers.pins) return;
                layers.pins.clearLayers();
                const bounds = [];
                (devices || []).forEach(d => {
                    if (!d.latest) return;
                    const color = STATUS_COLOR[d.status] || STATUS_COLOR.never;
                    const name = d.label || d.subject_name || d.kind;
                    const ll = [d.latest.lat, d.latest.lon];
                    const selected = d.id === selectedId;
                    if (selected) {
                        L.circle(ll, {
                            radius: Math.max(10, d.latest.accuracy_m || 20), color, weight: 2,
                            fillColor: color, fillOpacity: 0.12,
                        }).addTo(layers.pins);
                    }
                    const m = L.marker(ll, { icon: pinIcon(color, name, selected), zIndexOffset: selected ? 1000 : 0 });
                    m.on('click', () => { if (onSelectRef.current) onSelectRef.current(d.id); });
                    m.addTo(layers.pins);
                    bounds.push(ll);
                });
                if (!didFitRef.current && bounds.length) {
                    didFitRef.current = true;
                    if (bounds.length === 1) map.setView(bounds[0], 15);
                    else map.fitBounds(L.latLngBounds(bounds).pad(0.2), { maxZoom: 16 });
                }
            }, [devices, selectedId]);

            // Route (retrace) for the selected tracker.
            useEffect(() => {
                const map = mapRef.current, layers = layersRef.current;
                if (!map || !layers.route) return;
                layers.route.clearLayers();
                const all = route || [];
                if (!all.length) return;
                // A solid line only where the phone was actually reporting. A
                // silence (page closed, no signal, phone off) or an impossible
                // jump is drawn as a dashed grey connector, and rough cell-tower
                // fixes stay off the line so they cannot draw false spikes.
                const { segments, gaps, coarse } = splitRoute(all);
                coarse.forEach(p => {
                    L.circleMarker([p.lat, p.lon], { radius: 4, color: '#9a9aa2', weight: 1, fillColor: '#9a9aa2', fillOpacity: 0.25 })
                        .bindTooltip(`Rough fix ±${Math.round(p.accuracy_m)} m · ${new Date(p.recorded_at).toLocaleString('en-GB')}`).addTo(layers.route);
                });
                gaps.forEach(([a, b]) => {
                    L.polyline([[a.lat, a.lon], [b.lat, b.lon]], { color: '#9a9aa2', weight: 2, opacity: 0.6, dashArray: '6 8' })
                        .bindTooltip(`No data for ${fmtGap(a, b)} between these points`).addTo(layers.route);
                });
                segments.forEach(seg => {
                    if (seg.length > 1) {
                        L.polyline(seg.map(p => [p.lat, p.lon]), { color: ROUTE_COLOR, weight: 4, opacity: 0.8 }).addTo(layers.route);
                    } else {
                        // A lone fix (e.g. a stop-point check-in): show it as a dot.
                        L.circleMarker([seg[0].lat, seg[0].lon], { radius: 5, color: '#fff', weight: 1.5, fillColor: ROUTE_COLOR, fillOpacity: 0.9 })
                            .bindTooltip(new Date(seg[0].recorded_at).toLocaleString('en-GB')).addTo(layers.route);
                    }
                });
                const good = segments.flat();
                if (!good.length) return;
                const first = good[0], last = good[good.length - 1];
                L.circleMarker([first.lat, first.lon], { radius: 6, color: '#fff', weight: 2, fillColor: ROUTE_COLOR, fillOpacity: 1 })
                    .bindTooltip('Start · ' + new Date(first.recorded_at).toLocaleString('en-GB')).addTo(layers.route);
                if (good.length > 1) {
                    L.circleMarker([last.lat, last.lon], { radius: 5, color: '#fff', weight: 2, fillColor: '#ffffff', fillOpacity: 0.9 })
                        .bindTooltip('End · ' + new Date(last.recorded_at).toLocaleString('en-GB')).addTo(layers.route);
                }
            }, [route]);

            // Replay ghost marker.
            useEffect(() => {
                const layers = layersRef.current;
                if (!layers.ghost) return;
                layers.ghost.clearLayers();
                if (replayIndex == null || !route || !route[replayIndex]) return;
                const p = route[replayIndex];
                L.circleMarker([p.lat, p.lon], { radius: 8, color: '#0b0b0d', weight: 2, fillColor: '#ffd166', fillOpacity: 1 })
                    .bindTooltip(new Date(p.recorded_at).toLocaleString('en-GB')
                        + (p.speed_mps != null ? ` · ${(p.speed_mps * 3.6).toFixed(0)} km/h` : ''), { permanent: true, direction: 'top' })
                    .addTo(layers.ghost);
            }, [route, replayIndex]);

            // Fit-all / fit-route requests (counters bumped by the parent).
            useEffect(() => {
                const map = mapRef.current;
                if (!map || !fitAllKey) return;
                const b = (devices || []).filter(d => d.latest).map(d => [d.latest.lat, d.latest.lon]);
                if (b.length === 1) map.setView(b[0], 15);
                else if (b.length) map.fitBounds(L.latLngBounds(b).pad(0.2), { maxZoom: 16 });
            }, [fitAllKey]);  // eslint-disable-line react-hooks/exhaustive-deps
            useEffect(() => {
                const map = mapRef.current;
                if (!map || !fitRouteKey) return;
                const pts = (route || []).map(p => [p.lat, p.lon]);
                if (pts.length > 1) map.fitBounds(L.latLngBounds(pts).pad(0.15), { maxZoom: 17 });
                else if (pts.length === 1) map.setView(pts[0], 16);
            }, [fitRouteKey]);  // eslint-disable-line react-hooks/exhaustive-deps

            // Pan to a tracker when it is selected.
            useEffect(() => {
                const map = mapRef.current;
                if (!map || !selectedId) return;
                const d = (devices || []).find(x => x.id === selectedId);
                if (d && d.latest) map.setView([d.latest.lat, d.latest.lon], Math.max(map.getZoom(), 14));
            }, [selectedId]);  // eslint-disable-line react-hooks/exhaustive-deps

            return <div ref={ref} role="img" aria-label="Tracking map" className="tracking-map"
                        style={{ height, width: '100%', borderRadius: 'var(--radius-sm)',
                                 border: '1px solid var(--line)', background: '#0d1015' }} />;
        }

export { TrackingMap, STATUS_COLOR };
